import type { ApprovalGatedResult } from '@risksmart-app/trpc/src/helpers/with-approval-workflow';
import type { DeleteRiskMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  DeleteRiskDocument,
  namedOperations,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

export const useDeleteRisk = createMutationHook({
  name: 'deleteRisk',
  // risk.delete is single-gated through runWithApprovalWorkflow: it throws
  // CHANGE_REQUEST_REQUIRED when approval is needed (the other entity deletes
  // are bulk and return the partition directly). The governed path surfaces
  // the confirm modal and replays with _confirmChangeRequest.
  isChangeRequestGated: true,
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.risk.delete.mutationOptions({ onSuccess }),
  mapInput: (id: string) => ({ id }),
  refreshChangeRequestFeedsWhen: (result: ApprovalGatedResult<unknown>) =>
    result.status !== 'applied',
  invalidate: (trpc) => [
    trpc.frontend.risk.register.queryKey(),
    trpc.frontend.risk.riskById.queryKey(),
    trpc.frontend.risk.scores.queryKey(),
    trpc.frontend.risk.riskListOnlyOptimized.queryKey(),
    trpc.frontend.risk.riskListOnlyWithEntitiesOptimized.queryKey(),
    trpc.frontend.risk.riskScoresByRiskId.queryKey(),
    // COI-471 parity: Apollo twin also evicts `acceptance`, `appetite` and
    // `impact_rating` (all hang off the deleted risk).
    trpc.frontend.acceptance.pathKey(),
    trpc.frontend.appetite.pathKey(),
    trpc.frontend.impactRating.pathKey(),
  ],
  // trpc throws on 0 affected rows; on the change-request branch no row is
  // deleted yet (a CR was raised) but the action still "succeeded" from the
  // caller's perspective, mirroring the legacy delete flow.
  mapResponse: (): DeleteRiskMutation => ({
    deleteRiskById: {
      __typename: 'GenericMutationOutput',
      affected_rows: 1,
    },
  }),
  graphqlDocument: DeleteRiskDocument,
  graphqlVariables: (id: string) => ({ id }),
  evictFields: [
    'risk',
    'risk_aggregate',
    'acceptance',
    'appetite',
    'appetite_aggregate',
    'impact_rating',
  ],
  refetchQueries: [
    namedOperations.Query.getRiskById,
    namedOperations.Query.getRisksByTier,
  ],
});
